import React, { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Camera, CameraOff, ScanLine, X, Flashlight, RefreshCw,
  Smartphone, Monitor, Check, AlertCircle
} from "lucide-react";

/**
 * BarcodeScanner - Componente de câmera PWA para escaneamento de código de barras
 * Usa a API MediaDevices para acessar a câmera do dispositivo
 * Suporta leitura de EAN-13, EAN-8, Code128, Code39
 */
const BarcodeScanner = ({ onScan, onClose, isOpen }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // 'environment' = traseira, 'user' = frontal
  const [torchOn, setTorchOn] = useState(false);
  const [lastScanned, setLastScanned] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');

  // Iniciar câmera quando modal abrir
  useEffect(() => {
    if (isOpen) {
      startCamera();
    } else {
      stopCamera();
    }
    
    return () => stopCamera();
  }, [isOpen]);

  // Função para iniciar a câmera
  const startCamera = async () => {
    setCameraError(null);
    setScanning(true);
    
    try {
      // Verificar se o navegador suporta
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Seu navegador não suporta acesso à câmera');
      }

      // Configurações da câmera
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraActive(true);
        
        // Iniciar detecção de código de barras
        startBarcodeDetection();
      }
    } catch (error) {
      console.error('Erro ao acessar câmera:', error);
      setCameraError(
        error.name === 'NotAllowedError' 
          ? 'Permissão de câmera negada. Por favor, permita o acesso à câmera.' 
          : error.name === 'NotFoundError'
            ? 'Nenhuma câmera encontrada no dispositivo.'
            : `Erro ao acessar câmera: ${error.message}`
      );
      setCameraActive(false);
    } finally {
      setScanning(false);
    }
  };

  // Função para parar a câmera
  const stopCamera = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setCameraActive(false);
  };

  // Alternar câmera frontal/traseira
  const toggleCamera = async () => {
    const newMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newMode);
    stopCamera();
    setTimeout(() => startCamera(), 100);
  };

  // Alternar lanterna (flash)
  const toggleTorch = async () => {
    if (!streamRef.current) return;
    
    const track = streamRef.current.getVideoTracks()[0];
    if (track && track.getCapabilities) {
      const capabilities = track.getCapabilities();
      if (capabilities.torch) {
        try {
          await track.applyConstraints({
            advanced: [{ torch: !torchOn }]
          });
          setTorchOn(!torchOn);
        } catch (error) {
          toast.error('Lanterna não disponível neste dispositivo');
        }
      } else {
        toast.error('Lanterna não suportada neste dispositivo');
      }
    }
  };

  // Detecção de código de barras usando BarcodeDetector API (se disponível)
  const startBarcodeDetection = () => {
    // Verificar se BarcodeDetector está disponível (Chrome, Edge)
    if ('BarcodeDetector' in window) {
      const barcodeDetector = new window.BarcodeDetector({
        formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e', 'qr_code']
      });

      scanIntervalRef.current = setInterval(async () => {
        if (videoRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
          try {
            const barcodes = await barcodeDetector.detect(videoRef.current);
            if (barcodes.length > 0) {
              const barcode = barcodes[0];
              handleBarcodeDetected(barcode.rawValue);
            }
          } catch (error) {
            // Silenciar erros de detecção
          }
        }
      }, 200); // Verificar a cada 200ms
    } else {
      // Fallback: mostrar mensagem para digitar manualmente
      console.log('BarcodeDetector API não disponível. Use entrada manual.');
    }
  };

  // Quando um código é detectado
  const handleBarcodeDetected = (code) => {
    if (code === lastScanned) return; // Evitar duplicatas
    
    setLastScanned(code);
    
    // Vibrar o dispositivo (se suportado)
    if (navigator.vibrate) {
      navigator.vibrate(100);
    }
    
    // Tocar som de sucesso
    playBeep();
    
    // Enviar código para o componente pai
    if (onScan) {
      onScan(code);
    }
    
    // Reset após 1 segundo para permitir nova leitura do mesmo código
    setTimeout(() => setLastScanned(null), 1000);
  };

  // Som de beep
  const playBeep = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 1000;
      oscillator.type = 'sine';
      gainNode.gain.value = 0.1;
      
      oscillator.start();
      setTimeout(() => oscillator.stop(), 100);
    } catch (e) {}
  };

  // Enviar código manual
  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualCode.trim()) {
      handleBarcodeDetected(manualCode.trim());
      setManualCode('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/95 backdrop-blur-sm flex items-center justify-center overflow-y-auto py-8 z-50 p-4">
      <Card className="bg-gray-900 border-gray-700 max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-orange-500" />
            <h2 className="text-lg font-bold text-white">Scanner de Código de Barras</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Área da Câmera */}
        <div className="relative aspect-video bg-black">
          {cameraError ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mb-3" />
              <p className="text-red-400 mb-4">{cameraError}</p>
              <Button onClick={startCamera} className="bg-orange-500 hover:bg-orange-600">
                <RefreshCw className="w-4 h-4 mr-2" />
                Tentar Novamente
              </Button>
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                playsInline
                muted
                autoPlay
              />
              
              {/* Overlay com guia de escaneamento */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-64 h-32 border-2 border-orange-500 rounded-lg relative">
                  <div className="absolute inset-0 border-4 border-transparent">
                    {/* Cantos destacados */}
                    <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-orange-500 rounded-tl" />
                    <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-orange-500 rounded-tr" />
                    <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-orange-500 rounded-bl" />
                    <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-orange-500 rounded-br" />
                  </div>
                  
                  {/* Linha de scan animada */}
                  {cameraActive && (
                    <div className="absolute inset-x-2 h-0.5 bg-orange-500 animate-pulse top-1/2 -translate-y-1/2" />
                  )}
                </div>
              </div>

              {/* Indicador de escaneamento */}
              {cameraActive && (
                <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/50 px-3 py-1 rounded-full">
                  <span className="text-green-400 text-sm flex items-center gap-2">
                    <ScanLine className="w-4 h-4 animate-pulse" />
                    Escaneando...
                  </span>
                </div>
              )}

              {/* Último código lido */}
              {lastScanned && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-green-500 px-4 py-2 rounded-lg">
                  <span className="text-white text-sm flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    {lastScanned}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Controles da Câmera */}
        <div className="p-4 border-b border-gray-700">
          <div className="flex justify-center gap-3">
            <Button
              onClick={toggleCamera}
              variant="outline"
              className="border-gray-700"
              disabled={!cameraActive}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {facingMode === 'environment' ? 'Câmera Frontal' : 'Câmera Traseira'}
            </Button>
            <Button
              onClick={toggleTorch}
              variant="outline"
              className={`border-gray-700 ${torchOn ? 'bg-yellow-500/20 border-yellow-500' : ''}`}
              disabled={!cameraActive}
            >
              <Flashlight className={`w-4 h-4 mr-2 ${torchOn ? 'text-yellow-400' : ''}`} />
              Lanterna
            </Button>
          </div>
        </div>

        {/* Entrada Manual */}
        <div className="p-4">
          <p className="text-gray-400 text-sm mb-2 text-center">
            Ou digite o código manualmente:
          </p>
          <form onSubmit={handleManualSubmit} className="flex gap-2">
            <input
              type="text"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="Digite o EAN ou SKU..."
              className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
            />
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
              <ScanLine className="w-4 h-4 mr-2" />
              Enviar
            </Button>
          </form>
        </div>

        {/* Dica */}
        <div className="p-4 pt-0">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
            <p className="text-blue-400 text-xs flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              Posicione o código de barras dentro da área destacada para escanear automaticamente
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default BarcodeScanner;
